import React from "react";
import modelingResults from "../data/modeling/modelingResults.json";
import modelComparisonImg from "../assets/images/modeling/model-rmse-comparison.png";
import correlationHeatmapImg from "../assets/images/modeling/monthly-correlation-heatmap.png";
import monthlyHoldoutImg from "../assets/images/modeling/monthly-holdout-fit.png";
import dailySeasonErrorImg from "../assets/images/modeling/daily-season-error.png";

const figureCards = [
  {
    title: "Holdout RMSE Comparison",
    image: modelComparisonImg,
    alt: "Bar chart comparing holdout RMSE across linear regression, random forest, and neural network models",
    caption:
      "The page uses a chronological holdout: earlier rows for training, most recent rows for testing. Season-aware random forests delivered the best overall holdout scores, while the neural network stayed competitive on daily data.",
  },
  {
    title: "Monthly Feature Correlations",
    image: correlationHeatmapImg,
    alt: "Heatmap showing monthly correlations between weather features and soil temperature",
    caption:
      "Air temperature dominates the signal, solar radiation adds useful context, and precipitation contributes far less than temperature or seasonal structure.",
  },
  {
    title: "Monthly Holdout Fit",
    image: monthlyHoldoutImg,
    alt: "Line chart of actual versus predicted monthly soil temperature on the holdout period",
    caption:
      "On the 2019-2023 holdout window, the season-aware monthly random forest tracked the underlying trend well without needing any live inference service.",
  },
  {
    title: "Warm vs Cold Error",
    image: dailySeasonErrorImg,
    alt: "Grouped bar chart comparing warm-season and cold-season RMSE for daily neural network and season-aware random forest models",
    caption:
      "Both strong daily models performed worse in cold months, which reinforces that a single undifferentiated story hides meaningful seasonal difficulty.",
  },
];

const metricColumns = [
  { key: "dataset", label: "Dataset" },
  { key: "model", label: "Model" },
  { key: "variant", label: "Variant" },
  { key: "rmse", label: "RMSE" },
  { key: "mae", label: "MAE" },
  { key: "r2", label: "R²" },
];

function formatDatasetLabel(value) {
  if (value === "daily") {
    return "Daily";
  }

  if (value === "monthly") {
    return "Monthly";
  }

  return value;
}

function renderInlineCode(text) {
  return text.split(/`([^`]+)`/g).map((part, index) =>
    index % 2 === 1 ? <code key={`${part}-${index}`}>{part}</code> : part
  );
}

const ModelingResultsPage = () => {
  const { datasets, highlights, metrics } = modelingResults;
  const bestDailyModel = metrics.find(
    (metric) => metric.dataset === "daily" && metric.model === "Random Forest" && metric.variant === "Season-aware"
  );
  const bestMonthlyModel = metrics.find(
    (metric) => metric.dataset === "monthly" && metric.model === "Random Forest" && metric.variant === "Season-aware"
  );

  return (
    <div className="page-content results-page">
      <h1>Modeling Results</h1>

      <div className="metric-strip">
        <div className="metric-tile">
          <span>Daily Rows</span>
          <b>{datasets.daily.rowsUsable}</b>
        </div>
        <div className="metric-tile">
          <span>Best Daily RMSE</span>
          <b>{bestDailyModel?.rmse.toFixed(2)}</b>
        </div>
        <div className="metric-tile">
          <span>Best Monthly RMSE</span>
          <b>{bestMonthlyModel?.rmse.toFixed(2)}</b>
        </div>
      </div>

      <div className="weather-container">
        <div className="weather-group results-card-full">
          <div className="weather-header">
            <h2>Project Focus</h2>
          </div>
          <div className="results-copy">
            <p>
              This page summarizes the methods and findings from our soil temperature modeling work.
            </p>
            <p>
              Soil temperature is a useful agricultural signal because it affects germination,
              frost timing, and day-to-day planting decisions, but it is harder to measure
              consistently than standard weather data.
            </p>
            <p>
              The target was soil temperature at 10 cm depth (<code>s10t</code>).
            </p>
            <p>
              We modeled soil temperature from four weather features: air temperature{" "}
              (<code>airt</code>), precipitation (<code>prec</code>), solar radiation{" "}
              (<code>slrt</code>), and wind speed (<code>wspd</code>).
            </p>
          </div>
        </div>
      </div>

      <div className="weather-container">
        <div className="weather-group">
          <div className="weather-header">
            <h3>Daily Dataset</h3>
          </div>
          <div className="results-copy">
            <p>
              <b>{datasets.daily.rowsUsable}</b> usable daily rows from <b>{datasets.daily.dateStart}</b> to{" "}
              <b>{datasets.daily.dateEnd}</b>.
            </p>
            <p>
              Holdout window: <b>{datasets.daily.testStart}</b> to <b>{datasets.daily.testEnd}</b> (
              {datasets.daily.testRows} rows).
            </p>
            <p>
              Coverage after dropping missing target/input rows: <b>{datasets.daily.usablePct}%</b>.
            </p>
          </div>
        </div>

        <div className="weather-group">
          <div className="weather-header">
            <h3>Monthly Dataset</h3>
          </div>
          <div className="results-copy">
            <p>
              <b>{datasets.monthly.rowsUsable}</b> usable monthly rows from{" "}
              <b>{datasets.monthly.dateStart}</b> to <b>{datasets.monthly.dateEnd}</b>.
            </p>
            <p>
              Holdout window: <b>{datasets.monthly.testStart}</b> to <b>{datasets.monthly.testEnd}</b> (
              {datasets.monthly.testRows} rows).
            </p>
            <p>
              Coverage after dropping missing target/input rows: <b>{datasets.monthly.usablePct}%</b>.
            </p>
          </div>
        </div>

        <div className="weather-group">
          <div className="weather-header">
            <h3>Methods Used</h3>
          </div>
          <div className="results-copy">
            <p>
              This page reflects the main model families explored in the project: linear
              regression, random forest, and a two-hidden-layer neural network used in the daily
              workflow.
            </p>
            <p>
              The project compares multiple supervised approaches, reports holdout performance
              directly, and uses the dashboard to surface the clearest findings from that analysis.
            </p>
          </div>
        </div>
      </div>

      <div className="weather-container">
        <div className="weather-group results-card-full">
          <div className="weather-header">
            <h2>Model Comparison</h2>
          </div>
          <div className="results-table-wrap">
            <table className="results-table">
              <thead>
                <tr>
                  {metricColumns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric) => (
                  <tr key={`${metric.dataset}-${metric.model}-${metric.variant}`}>
                    <td>{formatDatasetLabel(metric.dataset)}</td>
                    <td>{metric.model}</td>
                    <td>{metric.variant}</td>
                    <td>{metric.rmse.toFixed(2)}</td>
                    <td>{metric.mae.toFixed(2)}</td>
                    <td>{metric.r2.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="weather-container">
        {highlights.map((highlight) => (
          <div className="weather-group" key={highlight.title}>
            <div className="weather-header">
              <h3>{highlight.title}</h3>
            </div>
            <div className="results-copy">
              <p>{renderInlineCode(highlight.detail)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="results-figure-grid">
        {figureCards.map((figure) => (
          <div className="weather-group" key={figure.title}>
            <div className="weather-header">
              <h3>{figure.title}</h3>
            </div>
            <div className="results-figure-wrap">
              <img className="results-figure-img" src={figure.image} alt={figure.alt} />
              <p className="results-caption">{figure.caption}</p>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default ModelingResultsPage;
